import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { HandsetDialogService } from '../../../../../shared/handset-dialog.service';
import { ComplianceErrorCategory } from '../../../../../shared/plan-reviewer.service';
import { ArchitecturalPlanPage } from '../../../../../shared/models/architectural-plan-page.model';
import { CodeApplicabilityDialogComponent } from '../code-applicability-dialog/code-applicability-dialog.component';

/**
 * Fallback UI component for compliance tab when code compliance features are unavailable.
 * Phase 3 enhancement for graceful error handling.
 */
@Component({
  selector: 'app-compliance-fallback',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
  ],
  templateUrl: './compliance-fallback.component.html',
  styleUrls: ['./compliance-fallback.component.scss'],
})
export class ComplianceFallbackComponent {
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);

  @Input() errorCategory!: ComplianceErrorCategory;
  @Input() activePlanPage?: ArchitecturalPlanPage;
  @Output() retry = new EventEmitter<void>();
  @Output() contactSupport = new EventEmitter<void>();
  @Output() startInsights = new EventEmitter<string>();

  showTechnicalDetails = false;
  selectedInsightType = '';

  insightOptions = [
    { value: 'relevance', label: 'Evaluate relevance of compliance code to this page' },
    { value: 'compliance', label: 'Evaluate compliance against specific sections of the code' },
    { value: 'checklist', label: 'Evaluate compliance against a checklist (coming soon)', disabled: true },
  ];

  getTitle(): string {
    switch (this.errorCategory.type) {
      case 'ANALYSIS_NOT_AVAILABLE':
        return 'Compliance Analysis Not Available';
      case 'BACKEND_INITIALIZATION':
        return 'System Initializing';
      case 'PERMISSION_DENIED':
        return 'Access Restricted';
      case 'NOT_FOUND':
        return 'Data Not Found';
      case 'SERVICE_UNAVAILABLE':
        return 'Service Temporarily Unavailable';
      default:
        return 'Unable to Load Compliance Data';
    }
  }

  getSubtitle(): string {
    if (this.activePlanPage) {
      return `Analysis not available for page ${this.activePlanPage.getPageNumber()}: ${this.activePlanPage.getTitle()}`;
    }
    return this.errorCategory.userMessage;
  }

  getErrorMessage(): string {
    switch (this.errorCategory.type) {
      case 'BACKEND_INITIALIZATION':
        return 'The code compliance system is currently initializing. This usually takes a few moments. Please wait and try again shortly.';
      case 'PERMISSION_DENIED':
        return "You don't have the necessary permissions to access code compliance features for this project. Please contact your administrator if you believe this is an error.";
      case 'SERVICE_UNAVAILABLE':
        return 'The code compliance service is temporarily unavailable. Our team has been notified and is working to restore service.';
      default:
        return 'An unexpected error occurred while loading code compliance features. Please try again or contact support if the problem persists.';
    }
  }

  getIconForSeverity(severity: string): string {
    switch (severity) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'help';
    }
  }

  onRetry(): void {
    this.retry.emit();
  }

  onContactSupport(): void {
    this.contactSupport.emit();
  }

  onStartInsights(): void {
    if (this.selectedInsightType && this.activePlanPage) {
      const dialogRef = this.handsetDialog.open(CodeApplicabilityDialogComponent, {
        width: '800px',
        maxHeight: '90vh',
        disableClose: true,
        data: {
          activePlanPage: this.activePlanPage,
        },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // Success
          this.startInsights.emit(this.selectedInsightType);
        }
      });
    }
  }

  toggleTechnicalDetails(): void {
    this.showTechnicalDetails = !this.showTechnicalDetails;
  }
}
